import React, { useEffect, useMemo, useRef, useState } from "react";

import { FileText, Send, Trash2 } from "lucide-react";

import Button from "../../Components/Button";
import CustomCheckbox from "../../Components/CheckCircle";
import CommonInput from "../../Components/Input";
import CommonModal from "../../Components/Modal";
import MultiSelect, { Option } from "../../Components/MultiSelect";
import RichTextEditor from "../../Components/RichTextEditor";
import Select from "../../Components/Select";
import TabsComponent from "../../Components/Tab";
import Table from "../../Components/Table";
import { useAppDispatch } from "../../Store/Hooks";
import {
  deleteReleaseNoteVersion,
  fetchReleaseNoteVersions,
  fetchReleaseNotesRecipients,
  previewReleaseNotesEmail,
  ReleaseNoteVersion,
  ReleaseNotesOrgRecipientGroup,
  saveReleaseNoteVersion,
  sendReleaseNotesEmail,
} from "../../Store/Reducers/AdminSlice";
import { EmailTemplatePreviewItem } from "../../Store/Reducers/EmailActivitySlice";
import { ROLES } from "../../Utils/Constants/RoleConstants";
import { formatDateUS } from "../../Utils/Helper/Helper";
import { toast } from "../../Utils/Helper/toast";
import { Organization } from "../AdminOverviewContainer";
import EmailTemplatePreviewModal from "../EmailActivityContainer/EmailTemplatePreviewModal";
import "./style.scss";

interface AdminReleaseNotesContainerProps {
  organizations: Organization[];
  initialOrgId?: string;
}

const ORG_ADMIN_ROLE = ROLES.VENDOR_ADMIN;
const MAX_RELEASE_NOTES_RECIPIENTS = 40;

const roleLabel = (role?: string | null): string => {
  if ((role || "").toLowerCase() === ORG_ADMIN_ROLE) return "Org Admin";
  if (!role) return "User";
  return role.replace(/_/g, " ").replace(/\b\w/g, char => char.toUpperCase());
};

const hasVisibleText = (html: string): boolean =>
  html
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .trim().length > 0;

const lastLoginLabel = (value?: string | null): string => {
  if (!value) return "Never";
  return formatDateUS(value);
};

const RELEASE_NOTES_SUB_TABS = [
  { id: "versions", label: "Versions", icon: <FileText size={16} /> },
  { id: "send", label: "Send", icon: <Send size={16} /> },
];

const AdminReleaseNotesContainer: React.FC<AdminReleaseNotesContainerProps> = ({
  organizations,
  initialOrgId = "",
}) => {
  const dispatch = useAppDispatch();
  const appliedInitialOrg = useRef(false);
  const [view, setView] = useState<"versions" | "send">(
    initialOrgId ? "send" : "versions"
  );

  const [versionSearch, setVersionSearch] = useState("");
  const [debouncedVersionSearch, setDebouncedVersionSearch] = useState("");
  const [versions, setVersions] = useState<ReleaseNoteVersion[]>([]);
  const [catalog, setCatalog] = useState<ReleaseNoteVersion[]>([]);
  const [versionsLoading, setVersionsLoading] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [versionLabel, setVersionLabel] = useState("");
  const [versionTitle, setVersionTitle] = useState("");
  const [versionBody, setVersionBody] = useState("");
  const [savingVersion, setSavingVersion] = useState(false);

  const [selectedVersionId, setSelectedVersionId] = useState("");
  const [subject, setSubject] = useState("");
  const [headerBanner, setHeaderBanner] = useState("");
  const [bodyHtml, setBodyHtml] = useState("");
  const [selectedOrgs, setSelectedOrgs] = useState<Option[]>([]);
  const [groups, setGroups] = useState<ReleaseNotesOrgRecipientGroup[]>([]);
  const [userSearch, setUserSearch] = useState<Record<string, string>>({});
  const [roleFilter, setRoleFilter] = useState<Record<string, string>>({});
  const [selectedUserIds, setSelectedUserIds] = useState<
    Record<string, string[]>
  >({});
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendConfirmOpen, setSendConfirmOpen] = useState(false);
  const [versionToDelete, setVersionToDelete] =
    useState<ReleaseNoteVersion | null>(null);
  const [deletingVersion, setDeletingVersion] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewTemplate, setPreviewTemplate] =
    useState<EmailTemplatePreviewItem | null>(null);

  useEffect(() => {
    const timer = window.setTimeout(
      () => setDebouncedVersionSearch(versionSearch),
      400
    );
    return () => window.clearTimeout(timer);
  }, [versionSearch]);

  useEffect(() => {
    let cancelled = false;
    dispatch(fetchReleaseNoteVersions(undefined))
      .unwrap()
      .then(payload => {
        if (!cancelled) setCatalog(payload.items || []);
      })
      .catch(error => {
        if (!cancelled) {
          toast.error(
            typeof error === "string"
              ? error
              : "Failed to load release notes versions"
          );
        }
      });
    return () => {
      cancelled = true;
    };
  }, [dispatch]);

  useEffect(() => {
    let cancelled = false;
    setVersionsLoading(true);
    dispatch(fetchReleaseNoteVersions(debouncedVersionSearch))
      .unwrap()
      .then(payload => {
        if (!cancelled) setVersions(payload.items || []);
      })
      .catch(error => {
        if (!cancelled) {
          toast.error(
            typeof error === "string"
              ? error
              : "Failed to load release notes versions"
          );
        }
      })
      .finally(() => {
        if (!cancelled) setVersionsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [dispatch, debouncedVersionSearch]);

  useEffect(() => {
    if (appliedInitialOrg.current || !initialOrgId) return;
    const match = organizations.find(org => org.tenant_id === initialOrgId);
    if (!match) return;
    appliedInitialOrg.current = true;
    setView("send");
    setSelectedOrgs([{ value: match.tenant_id, label: match.organization }]);
  }, [initialOrgId, organizations]);

  const orgKey = selectedOrgs.map(org => org.value).join(",");

  useEffect(() => {
    if (!orgKey) {
      setGroups([]);
      setSelectedUserIds({});
      return;
    }
    let cancelled = false;
    setLoadingUsers(true);
    dispatch(fetchReleaseNotesRecipients(orgKey.split(",")))
      .unwrap()
      .then(payload => {
        if (cancelled) return;
        const next = payload.organizations || [];
        setGroups(next);
        setSelectedUserIds(current => {
          const kept: Record<string, string[]> = {};
          next.forEach(group => {
            const allowed = new Set(group.users.map(user => user.user_id));
            kept[group.org_id] = (current[group.org_id] || []).filter(id =>
              allowed.has(id)
            );
          });
          return kept;
        });
      })
      .catch(error => {
        if (!cancelled) {
          toast.error(
            typeof error === "string"
              ? error
              : "Failed to load organization users"
          );
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingUsers(false);
      });
    return () => {
      cancelled = true;
    };
  }, [dispatch, orgKey]);

  const orgOptions = useMemo(
    () =>
      organizations.map(org => ({
        value: org.tenant_id,
        label: org.organization,
      })),
    [organizations]
  );

  const versionOptions = useMemo(
    () =>
      catalog.map(version => ({
        value: version.id,
        label: `${version.version_label} · ${version.title}`,
      })),
    [catalog]
  );

  const selectedCount = Object.values(selectedUserIds).reduce(
    (total, ids) => total + ids.length,
    0
  );
  const canSaveVersion =
    versionLabel.trim().length > 0 &&
    versionTitle.trim().length > 0 &&
    hasVisibleText(versionBody) &&
    !savingVersion;
  const canPreview =
    subject.trim().length > 0 &&
    headerBanner.trim().length > 0 &&
    hasVisibleText(bodyHtml) &&
    !previewLoading &&
    !sending;
  const canSend =
    canPreview &&
    selectedCount > 0 &&
    selectedCount <= MAX_RELEASE_NOTES_RECIPIENTS &&
    !loadingUsers;

  const startNewVersion = () => {
    setEditingId(null);
    setVersionLabel("");
    setVersionTitle("");
    setVersionBody("");
  };

  const loadVersion = (version: ReleaseNoteVersion) => {
    setEditingId(version.id);
    setVersionLabel(version.version_label);
    setVersionTitle(version.title);
    setVersionBody(version.body_html);
  };

  const handleSaveVersion = async () => {
    if (!canSaveVersion) return;
    setSavingVersion(true);
    try {
      const saved = await dispatch(
        saveReleaseNoteVersion({
          id: editingId || undefined,
          version_label: versionLabel.trim(),
          title: versionTitle.trim(),
          body_html: versionBody,
        })
      ).unwrap();
      setEditingId(saved.id);
      toast.success(
        editingId
          ? "Release notes version updated."
          : "Release notes version saved."
      );
      await refreshVersionLists();
    } catch (error) {
      toast.error(
        typeof error === "string"
          ? error
          : "Failed to save the release notes version."
      );
    } finally {
      setSavingVersion(false);
    }
  };

  const handleVersionPick = (versionId: string) => {
    setSelectedVersionId(versionId);
    if (!versionId) {
      setSubject("");
      setHeaderBanner("");
      setBodyHtml("");
      return;
    }
    const version = catalog.find(item => item.id === versionId);
    if (!version) return;
    setSubject(version.title);
    setHeaderBanner(version.title);
    setBodyHtml(version.body_html);
  };

  const refreshVersionLists = async () => {
    const payload = await dispatch(
      fetchReleaseNoteVersions(debouncedVersionSearch)
    ).unwrap();
    setVersions(payload.items || []);
    const full = await dispatch(fetchReleaseNoteVersions(undefined)).unwrap();
    setCatalog(full.items || []);
  };

  const selectedRecipientsByOrg = useMemo(() => {
    return groups
      .map(group => {
        const ids = new Set(selectedUserIds[group.org_id] || []);
        const users = group.users
          .filter(user => ids.has(user.user_id))
          .map(user => ({
            user_id: user.user_id,
            full_name: user.full_name,
            email: user.email,
            role: roleLabel(user.role),
          }));
        return {
          org_id: group.org_id,
          organization: group.organization,
          users,
        };
      })
      .filter(group => group.users.length > 0);
  }, [groups, selectedUserIds]);

  const totalSelected = (map: Record<string, string[]>) =>
    Object.values(map).reduce((total, ids) => total + ids.length, 0);

  const toastRecipientCap = () => {
    toast.error(`Select at most ${MAX_RELEASE_NOTES_RECIPIENTS} recipients.`);
  };

  const toggleUser = (orgId: string, userId: string) => {
    let hitCap = false;
    setSelectedUserIds(current => {
      const ids = current[orgId] || [];
      if (ids.includes(userId)) {
        return { ...current, [orgId]: ids.filter(id => id !== userId) };
      }
      if (totalSelected(current) >= MAX_RELEASE_NOTES_RECIPIENTS) {
        hitCap = true;
        return current;
      }
      return { ...current, [orgId]: [...ids, userId] };
    });
    if (hitCap) toastRecipientCap();
  };

  const selectShown = (
    group: ReleaseNotesOrgRecipientGroup,
    shownIds: string[]
  ) => {
    let hitCap = false;
    setSelectedUserIds(current => {
      const selected = new Set(current[group.org_id] || []);
      // Remaining slots across the whole send (not "per org capacity").
      let room = MAX_RELEASE_NOTES_RECIPIENTS - totalSelected(current);
      for (const id of shownIds) {
        if (selected.has(id)) continue;
        if (room <= 0) {
          hitCap = true;
          break;
        }
        selected.add(id);
        room -= 1;
      }
      if (shownIds.some(id => !selected.has(id))) {
        hitCap = true;
      }
      return { ...current, [group.org_id]: Array.from(selected) };
    });
    if (hitCap) toastRecipientCap();
  };

  const deselectAllInOrg = (orgId: string) => {
    setSelectedUserIds(current => ({ ...current, [orgId]: [] }));
  };

  const toggleShown = (
    group: ReleaseNotesOrgRecipientGroup,
    shownIds: string[]
  ) => {
    const selected = new Set(selectedUserIds[group.org_id] || []);
    const allShown =
      shownIds.length > 0 && shownIds.every(id => selected.has(id));
    if (allShown) {
      setSelectedUserIds(current => {
        const next = new Set(current[group.org_id] || []);
        shownIds.forEach(id => next.delete(id));
        return { ...current, [group.org_id]: Array.from(next) };
      });
      return;
    }
    selectShown(group, shownIds);
  };

  const handlePreview = async () => {
    if (!canPreview) return;
    setPreviewLoading(true);
    try {
      const preview = await dispatch(
        previewReleaseNotesEmail({
          subject: subject.trim(),
          header_banner: headerBanner.trim(),
          body_html: bodyHtml,
        })
      ).unwrap();
      setPreviewTemplate({
        email_type: "release_notes",
        name: preview.name || "Release Notes",
        subject: preview.subject || subject.trim(),
        html: preview.html,
      });
      setPreviewOpen(true);
    } catch (error) {
      toast.error(
        typeof error === "string"
          ? error
          : "Failed to preview the release notes email."
      );
    } finally {
      setPreviewLoading(false);
    }
  };

  const openSendConfirm = () => {
    if (!canSend) return;
    if (selectedCount > MAX_RELEASE_NOTES_RECIPIENTS) {
      toast.error(`Select at most ${MAX_RELEASE_NOTES_RECIPIENTS} recipients.`);
      return;
    }
    setSendConfirmOpen(true);
  };

  const handleConfirmSend = async () => {
    if (!canSend) return;
    if (selectedCount > MAX_RELEASE_NOTES_RECIPIENTS) {
      toast.error(`Select at most ${MAX_RELEASE_NOTES_RECIPIENTS} recipients.`);
      return;
    }
    const recipients = selectedRecipientsByOrg.map(group => ({
      org_id: group.org_id,
      user_ids: group.users.map(user => user.user_id),
    }));
    setSending(true);
    try {
      const result = await dispatch(
        sendReleaseNotesEmail({
          version_id: selectedVersionId || null,
          subject: subject.trim(),
          header_banner: headerBanner.trim(),
          body_html: bodyHtml,
          recipients,
        })
      ).unwrap();
      setSendConfirmOpen(false);
      const sent = result.sent_count || 0;
      const failed = result.failed_count || 0;
      if (failed > 0) {
        toast.error(
          `Release notes sent to ${sent} user${sent === 1 ? "" : "s"}. ${failed} could not be sent.`
        );
        return;
      }
      toast.success(
        `Release notes sent to ${sent} user${sent === 1 ? "" : "s"}.`
      );
    } catch (error) {
      toast.error(
        typeof error === "string"
          ? error
          : "Failed to send the release notes email."
      );
    } finally {
      setSending(false);
    }
  };

  const handleConfirmDeleteVersion = async () => {
    if (!versionToDelete) return;
    setDeletingVersion(true);
    try {
      await dispatch(deleteReleaseNoteVersion(versionToDelete.id)).unwrap();
      toast.success("Release notes version deleted.");
      if (editingId === versionToDelete.id) {
        startNewVersion();
      }
      if (selectedVersionId === versionToDelete.id) {
        setSelectedVersionId("");
        setSubject("");
        setHeaderBanner("");
        setBodyHtml("");
      }
      setVersionToDelete(null);
      await refreshVersionLists();
    } catch (error) {
      toast.error(
        typeof error === "string"
          ? error
          : "Failed to delete the release notes version."
      );
    } finally {
      setDeletingVersion(false);
    }
  };

  return (
    <div className="admin-release-notes">
      <div className="admin-release-notes__subtabs border-b border-gray-200">
        <TabsComponent
          tabs={RELEASE_NOTES_SUB_TABS}
          activeTab={view}
          onTabChange={id => setView(id as "versions" | "send")}
          variant="tab-underline"
          className="mb-0 border-b"
        />
      </div>

      {view === "versions" ? (
        <div className="admin-release-notes__versions">
          <div className="admin-release-notes__version-list">
            <CommonInput
              label="Search versions"
              name="release-notes-version-search"
              value={versionSearch}
              onChange={event => setVersionSearch(event.target.value)}
              placeholder="Search by label or title"
              variant="field"
            />
            <Button variant="outline-primary" onClick={startNewVersion}>
              New version
            </Button>
            {versionsLoading ? (
              <p className="admin-release-notes__hint">Loading versions...</p>
            ) : versions.length === 0 ? (
              <p className="admin-release-notes__hint">
                No release notes versions yet.
              </p>
            ) : (
              <ul className="admin-release-notes__version-items">
                {versions.map(version => (
                  <li key={version.id}>
                    <div
                      className={`admin-release-notes__version-card${
                        editingId === version.id ? " is-active" : ""
                      }`}
                    >
                      <button
                        type="button"
                        className="admin-release-notes__version-select"
                        onClick={() => loadVersion(version)}
                      >
                        <strong>{version.version_label}</strong>
                        <span>{version.title}</span>
                        <span className="admin-release-notes__version-meta">
                          Created {formatDateUS(version.created_at)}
                        </span>
                        <span className="admin-release-notes__version-meta">
                          Updated {formatDateUS(version.updated_at)}
                        </span>
                      </button>
                      <button
                        type="button"
                        className="admin-release-notes__version-delete"
                        title="Delete version"
                        aria-label={`Delete version ${version.version_label}`}
                        onClick={() => setVersionToDelete(version)}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className="admin-release-notes__version-form">
            <p className="admin-release-notes__hint">
              Updating this version changes it for future emails. Create a new
              version to keep the previous copy.
            </p>
            <CommonInput
              label="Version label"
              name="release-notes-version-label"
              value={versionLabel}
              onChange={event => setVersionLabel(event.target.value)}
              placeholder="2026.10"
              required
              variant="field"
              maxLength={80}
            />
            <CommonInput
              label="Title"
              name="release-notes-version-title"
              value={versionTitle}
              onChange={event => setVersionTitle(event.target.value)}
              placeholder="ExecBridge Release Notes"
              required
              variant="field"
              maxLength={200}
            />
            <p className="admin-release-notes__hint">
              Used as the email header banner (and prefilled into Subject on
              Send). Notes become the email body.
            </p>
            <div className="admin-release-notes__editor-label">Notes</div>
            <RichTextEditor
              value={versionBody}
              onChange={setVersionBody}
              ariaLabel="Release notes version content"
            />
            <div className="admin-release-notes__form-actions">
              <Button
                variant="primary"
                onClick={handleSaveVersion}
                loading={savingVersion}
                disabled={!canSaveVersion}
              >
                {editingId ? "Update version" : "Save version"}
              </Button>
            </div>
          </div>
        </div>
      ) : (
        <div className="admin-release-notes__send">
          <Select
            label="Release notes version"
            name="release-notes-version"
            value={selectedVersionId}
            onChange={event => handleVersionPick(String(event.target.value))}
            options={versionOptions}
            placeholder="Write fresh (no saved version)"
          />
          <p className="admin-release-notes__hint">
            Optional. Pick a saved version to fill subject, header banner, and
            body from the version title and notes, or leave this empty and write
            the note here.
          </p>
          <CommonInput
            label="Subject"
            name="release-notes-subject"
            value={subject}
            onChange={event => setSubject(event.target.value)}
            placeholder="ExecBridge Release Notes"
            required
            variant="field"
            maxLength={200}
          />
          <p className="admin-release-notes__hint">
            Inbox subject line for the email.
          </p>
          <CommonInput
            label="Email header banner"
            name="release-notes-header-banner"
            value={headerBanner}
            onChange={event => setHeaderBanner(event.target.value)}
            placeholder="ExecBridge Release Notes"
            required
            variant="field"
            maxLength={200}
          />
          <p className="admin-release-notes__hint">
            Purple banner title inside the email. Prefills from the version
            title; you can change it for this send only.
          </p>
          <div className="admin-release-notes__editor-label">Email content</div>
          <p className="admin-release-notes__hint">
            Write the announcement here. If you picked a version, edits on this
            screen apply to this send only and are not saved back. The standard
            header and footer are added when the email is sent.
          </p>
          <RichTextEditor
            value={bodyHtml}
            onChange={setBodyHtml}
            ariaLabel="Release notes announcement content"
          />
          <MultiSelect
            label="Organizations"
            required
            className="admin-release-notes__orgs"
            options={orgOptions}
            selected={selectedOrgs}
            onChange={setSelectedOrgs}
            placeholder="Search organizations..."
            showAvatar={false}
          />
          {loadingUsers ? (
            <p className="admin-release-notes__hint">Loading users...</p>
          ) : (
            groups.map(group => {
              const query = (userSearch[group.org_id] || "")
                .trim()
                .toLowerCase();
              const selectedRole = roleFilter[group.org_id] || "";
              const roleOptions = Array.from(
                new Map(
                  group.users.map(user => {
                    const value = (user.role || "").toLowerCase() || "user";
                    return [value, { value, label: roleLabel(user.role) }];
                  })
                ).values()
              ).sort((a, b) => a.label.localeCompare(b.label));
              const shown = group.users.filter(user => {
                const userRole = (user.role || "").toLowerCase() || "user";
                if (selectedRole && userRole !== selectedRole) return false;
                if (!query) return true;
                const haystack =
                  `${user.full_name} ${user.email} ${roleLabel(user.role)}`.toLowerCase();
                return haystack.includes(query);
              });
              const selected = new Set(selectedUserIds[group.org_id] || []);
              const shownIds = shown.map(user => user.user_id);
              const allShown =
                shownIds.length > 0 && shownIds.every(id => selected.has(id));
              return (
                <section
                  key={group.org_id}
                  className="admin-release-notes__org"
                >
                  <div className="admin-release-notes__org-head">
                    <h3>{group.organization}</h3>
                    <span>
                      {selected.size} selected · {group.users.length} active
                    </span>
                  </div>
                  <div className="admin-release-notes__filters">
                    <CommonInput
                      className="admin-release-notes__user-search"
                      label="Search users"
                      name={`user-search-${group.org_id}`}
                      value={userSearch[group.org_id] || ""}
                      onChange={event =>
                        setUserSearch(current => ({
                          ...current,
                          [group.org_id]: event.target.value,
                        }))
                      }
                      placeholder="Search by name or email"
                      variant="field"
                    />
                    <Select
                      className="admin-release-notes__role-filter"
                      label="Role"
                      name={`user-role-${group.org_id}`}
                      value={selectedRole}
                      onChange={event =>
                        setRoleFilter(current => ({
                          ...current,
                          [group.org_id]: String(event.target.value),
                        }))
                      }
                      options={roleOptions}
                      placeholder="All roles"
                    />
                  </div>
                  <Table
                    className="admin-release-notes__table"
                    pagination
                    defaultRowsPerPage={10}
                    rowKey={record => record.user_id}
                    emptyContent={
                      <p className="admin-release-notes__hint">
                        No active users match this search.
                      </p>
                    }
                    columns={[
                      {
                        key: "select",
                        width: "48px",
                        title: (
                          <CustomCheckbox
                            checked={allShown}
                            disabled={shownIds.length === 0}
                            onChange={() => toggleShown(group, shownIds)}
                          />
                        ),
                        render: (_: unknown, record) => (
                          <CustomCheckbox
                            checked={selected.has(record.user_id)}
                            onChange={() =>
                              toggleUser(group.org_id, record.user_id)
                            }
                          />
                        ),
                      },
                      {
                        key: "full_name",
                        title: "Name",
                      },
                      {
                        key: "email",
                        title: "Email",
                      },
                      {
                        key: "role",
                        title: "Role",
                        render: (value: string | null) => roleLabel(value),
                      },
                      {
                        key: "last_login_at",
                        title: "Last login",
                        render: (value: string | null) => lastLoginLabel(value),
                      },
                      {
                        key: "last_contacted_at",
                        title: "Last contacted",
                        render: (value: string | null) => lastLoginLabel(value),
                      },
                    ]}
                    data={shown}
                  />
                  <div className="admin-release-notes__selection-actions">
                    <button
                      type="button"
                      className="admin-release-notes__select-shown"
                      disabled={shownIds.length === 0}
                      onClick={() => selectShown(group, shownIds)}
                    >
                      Select all shown
                    </button>
                    <button
                      type="button"
                      className="admin-release-notes__select-shown"
                      disabled={selected.size === 0}
                      onClick={() => deselectAllInOrg(group.org_id)}
                    >
                      Deselect all
                    </button>
                  </div>
                </section>
              );
            })
          )}
          <div className="admin-release-notes__form-actions">
            <span className="admin-release-notes__hint">
              {selectedCount} / {MAX_RELEASE_NOTES_RECIPIENTS} recipient
              {selectedCount === 1 ? "" : "s"} selected. Each organization is
              emailed separately, with its users on BCC.
            </span>
            <Button
              variant="outline-primary"
              onClick={handlePreview}
              loading={previewLoading}
              disabled={!canPreview}
            >
              Preview
            </Button>
            <Button
              variant="primary"
              onClick={openSendConfirm}
              loading={sending}
              disabled={!canSend}
            >
              Send email
            </Button>
          </div>
        </div>
      )}

      <EmailTemplatePreviewModal
        isOpen={previewOpen}
        toggle={() => setPreviewOpen(false)}
        template={previewTemplate}
        badgeLabel="Release notes"
      />

      <CommonModal
        isOpen={sendConfirmOpen}
        toggle={() => {
          if (!sending) setSendConfirmOpen(false);
        }}
        title="Send this release notes email?"
        size="2xl"
        usePortal
        contentClassName="overflow-hidden flex flex-col max-h-[85vh]"
        footer={
          <div className="flex justify-end gap-2">
            <Button
              variant="outline-primary"
              disabled={sending}
              onClick={() => setSendConfirmOpen(false)}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              loading={sending}
              disabled={sending}
              onClick={() => void handleConfirmSend()}
            >
              Send email
            </Button>
          </div>
        }
      >
        <div className="admin-release-notes__confirm">
          <p className="admin-release-notes__hint">
            Are you sure you want to send this release notes email? Review the
            recipients below. Each organization is emailed separately, with its
            users on BCC.
          </p>
          <p className="admin-release-notes__confirm-subject">
            Subject: <strong>{subject.trim() || "—"}</strong>
          </p>
          <p className="admin-release-notes__confirm-subject">
            Header banner: <strong>{headerBanner.trim() || "—"}</strong>
          </p>
          {selectedRecipientsByOrg.map(group => (
            <section
              key={group.org_id}
              className="admin-release-notes__confirm-org"
            >
              <h4>
                {group.organization}{" "}
                <span>
                  ({group.users.length} recipient
                  {group.users.length === 1 ? "" : "s"})
                </span>
              </h4>
              <ul>
                {group.users.map(user => (
                  <li key={user.user_id}>
                    <strong>{user.full_name}</strong>
                    <span>
                      {user.email} · {user.role}
                    </span>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      </CommonModal>

      <CommonModal
        isOpen={!!versionToDelete}
        toggle={() => {
          if (!deletingVersion) setVersionToDelete(null);
        }}
        title="Delete this release notes version?"
        size="md"
        usePortal
        footer={
          <div className="flex justify-end gap-2">
            <Button
              variant="outline-primary"
              disabled={deletingVersion}
              onClick={() => setVersionToDelete(null)}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              loading={deletingVersion}
              disabled={deletingVersion}
              onClick={() => void handleConfirmDeleteVersion()}
            >
              Delete version
            </Button>
          </div>
        }
      >
        {versionToDelete ? (
          <div className="admin-release-notes__confirm space-y-2 text-sm text-gray-600">
            <p className="m-0">
              Are you sure you want to delete this version? This cannot be
              undone.
            </p>
            <p className="m-0">
              Version:{" "}
              <strong className="text-gray-900">
                {versionToDelete.version_label}
              </strong>
            </p>
            <p className="m-0">
              Title:{" "}
              <strong className="text-gray-900">{versionToDelete.title}</strong>
            </p>
            <p className="m-0 text-xs text-gray-500">
              Created on {formatDateUS(versionToDelete.created_at)}
            </p>
          </div>
        ) : null}
      </CommonModal>
    </div>
  );
};

export default AdminReleaseNotesContainer;
